import React from 'react';
import PlatformIcon from '../../../../components/common/PlatformIcon';
import { SocialPost } from '../../../../types/socialMediaPlanner';

interface AnalyticsViewProps {
  posts: SocialPost[];
}

/**
 * Analytics View Component
 * Displays engagement metrics for published posts
 * This is a simple placeholder that will be enhanced with charts and more data
 */
const AnalyticsView: React.FC<AnalyticsViewProps> = ({ posts }) => {
  const publishedPosts = posts.filter(post => post.status === 'published');

  // Calculate platform totals
  const platformCounts = {
    facebook: publishedPosts.filter(post => post.platforms.includes('facebook')).length,
    twitter: publishedPosts.filter(post => post.platforms.includes('twitter')).length,
    linkedin: publishedPosts.filter(post => post.platforms.includes('linkedin')).length,
    instagram: publishedPosts.filter(post => post.platforms.includes('instagram')).length,
  };

  // Using shared PlatformIcon component instead of local function

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded shadow-sm">
          <h3 className="font-medium text-gray-500 mb-1">Total Posts</h3>
          <p className="text-2xl font-bold">{publishedPosts.length}</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm">
          <h3 className="font-medium text-gray-500 mb-1">Total Impressions</h3>
          <p className="text-2xl font-bold">0</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm">
          <h3 className="font-medium text-gray-500 mb-1">Total Engagement</h3>
          <p className="text-2xl font-bold">0</p>
        </div>
        <div className="bg-white p-4 rounded shadow-sm">
          <h3 className="font-medium text-gray-500 mb-1">Engagement Rate</h3>
          <p className="text-2xl font-bold">0%</p>
        </div>
      </div>

      {/* Platform Breakdown */}
      <div className="bg-white p-6 rounded shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Platform Breakdown</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-md">
              <span className="font-bold">f</span>
            </div>
            <div>
              <p className="font-bold">{platformCounts.facebook}</p>
              <p className="text-sm text-gray-500">Facebook Posts</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-400 text-white rounded-md">
              <span className="font-bold">t</span>
            </div>
            <div>
              <p className="font-bold">{platformCounts.twitter}</p>
              <p className="text-sm text-gray-500">Twitter Posts</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-800 text-white rounded-md">
              <span className="font-bold">in</span>
            </div>
            <div>
              <p className="font-bold">{platformCounts.linkedin}</p>
              <p className="text-sm text-gray-500">LinkedIn Posts</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-pink-500 text-white rounded-md">
              <span className="font-bold">ig</span>
            </div>
            <div>
              <p className="font-bold">{platformCounts.instagram}</p>
              <p className="text-sm text-gray-500">Instagram Posts</p>
            </div>
          </div>
        </div>
      </div>

      {/* Post Performance */}
      <div className="bg-white p-6 rounded shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Post Performance</h2>
        
        {publishedPosts.length === 0 ? (
          <p className="text-gray-500">No published posts yet to show analytics.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center justify-center w-4 h-4 mr-1">
                      <span className="inline-block w-3 h-3 border border-current rounded-full relative">
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="inline-block w-1.5 h-1.5 bg-current rounded-full"></span>
                        </span>
                      </span>
                    </span> Impressions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center justify-center w-4 h-4 mr-1">
                      <span className="inline-block w-2.5 h-2.5 border-t border-r border-current transform rotate-45"></span>
                    </span> Likes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center justify-center w-4 h-4 mr-1">
                      <span className="inline-block w-3 h-2.5 border border-current rounded-md"></span>
                    </span> Comments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center justify-center w-4 h-4 mr-1">
                      <span className="inline-block w-3 h-2.5 border border-current relative">
                        <span className="absolute top-0 right-0 w-1.5 h-1.5 border-l border-b border-current transform translate-x-0.5 -translate-y-0.5 rotate-45"></span>
                      </span>
                    </span> Shares
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {publishedPosts.map((post) => (
                  <tr key={post.id}>
                    <td className="px-6 py-4 whitespace-normal">
                      <div className="text-sm text-gray-900 max-w-xs truncate">{post.content}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-1">
                        {post.platforms.map((platform, idx) => (
                          <span key={idx}><PlatformIcon platform={platform} /></span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">0</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">0</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">0</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">0</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white p-6 rounded shadow-sm text-center">
        <p className="text-gray-500 italic">
          Note: Analytics data will be available once your posts have been published and engagement metrics have been collected.
        </p>
      </div>
    </div>
  );
};

export default AnalyticsView;
